import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pagesAPI, shiftsAPI } from '../utils/api';
import { LogOut, Clock, AlertTriangle, CheckCircle2, Power, PowerOff, UserCheck, ArrowRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const DoctorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewFilter, setReviewFilter] = useState('all'); // 'all', 'reviewed', 'unreviewed'
  const [activeShift, setActiveShift] = useState(null);
  const [onDutyDoctors, setOnDutyDoctors] = useState([]);
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showEndShiftModal, setShowEndShiftModal] = useState(false);

  useEffect(() => {
    fetchPages();
    fetchActiveShift();
    fetchOnDutyDoctors();
    const interval = setInterval(() => {
      fetchPages();
      fetchOnDutyDoctors();
    }, 5000); // Refresh every 5s
    return () => clearInterval(interval);
  }, []);

  const fetchPages = async () => {
    try {
      const response = await pagesAPI.getAll();
      setPages(response.data.filter(p => p.status !== 'completed' && p.status !== 'cancelled'));
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveShift = async () => {
    try {
      const response = await shiftsAPI.getActiveShift();
      setActiveShift(response.data);
    } catch (error) {
      console.error('Error fetching active shift:', error);
    }
  };

  const fetchOnDutyDoctors = async () => {
    try {
      const response = await shiftsAPI.getOnDutyDoctors();
      setOnDutyDoctors(response.data.filter(d => d.id !== user.id));
    } catch (error) {
      console.error('Error fetching on-duty doctors:', error);
    }
  };

  const handleStartShift = async () => {
    try {
      await shiftsAPI.startShift();
      await fetchActiveShift();
      await fetchOnDutyDoctors();
    } catch (error) {
      console.error('Error starting shift:', error);
      alert('Failed to start shift');
    }
  };

  const handleEndShiftClick = () => {
    const openPages = pages.filter(p => ['pending', 'acknowledged', 'in_progress'].includes(p.status));
    if (openPages.length > 0) {
      setShowEndShiftModal(true);
    } else {
      handleEndShift();
    }
  };

  const handleEndShift = async () => {
    try {
      await shiftsAPI.endShift();
      setActiveShift(null);
      setShowEndShiftModal(false);
      await fetchOnDutyDoctors();
    } catch (error) {
      console.error('Error ending shift:', error);
      if (error.response?.data?.openPages) {
        alert('Cannot end shift: You have open tasks that must be delegated first');
      } else {
        alert('Failed to end shift');
      }
    }
  };

  const handleDelegateClick = (page) => {
    setSelectedPage(page);
    setShowDelegateModal(true);
  };

  const handleDelegate = async (toDoctorId) => {
    try {
      await shiftsAPI.delegatePage(selectedPage.id, toDoctorId, 'Manual delegation');
      setShowDelegateModal(false);
      setSelectedPage(null);
      await fetchPages();
    } catch (error) {
      console.error('Error delegating task:', error);
      alert('Failed to delegate task');
    }
  };

  const handleBulkDelegate = async (toDoctorId) => {
    const openPages = pages.filter(p => ['pending', 'acknowledged', 'in_progress'].includes(p.status));
    const pageIds = openPages.map(p => p.id);
    
    try {
      await shiftsAPI.bulkDelegatePages(pageIds, toDoctorId, 'Shift handover');
      setShowEndShiftModal(false);
      await fetchPages();
      await handleEndShift();
    } catch (error) {
      console.error('Error delegating tasks:', error);
      alert('Failed to delegate tasks');
    }
  };

  // Apply review filter
  const filteredPages = pages.filter(page => {
    if (reviewFilter === 'reviewed') return page.reviewed === true;
    if (reviewFilter === 'unreviewed') return page.reviewed !== true;
    return true; // 'all'
  });

  // Eisenhower Matrix quadrants
  const doFirst = filteredPages.filter(p => p.priority === 1);
  const schedule = filteredPages.filter(p => p.priority === 2);
  const delegate = filteredPages.filter(p => p.priority === 3);
  const defer = filteredPages.filter(p => p.priority === 4);

  const PageCard = ({ page }) => (
    <div className="bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 transition-all hover:shadow-md relative">
      {page.reviewed && (
        <div className="absolute top-2 right-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" title="Clinically Reviewed" />
        </div>
      )}
      <div 
        onClick={() => navigate(`/page/${page.id}`)}
        className="cursor-pointer"
      >
        <div className="flex justify-between items-start mb-2 pr-6">
          <h4 className="font-semibold text-gray-900">URN: {page.patient.nhsNumber}</h4>
          {page.escalationReason && (
            <AlertTriangle className="w-5 h-5 text-red-600" />
          )}
        </div>
        <p className="text-sm text-gray-600 mb-2">{page.patient.location}</p>
        <p className="text-sm text-gray-700 line-clamp-2 mb-2">{page.reason}</p>
        
        {page.escalationReason && (
          <div className="bg-red-50 border border-red-200 rounded px-2 py-1 mb-2">
            <p className="text-xs text-red-800 font-medium">⚠️ {page.escalationReason.split(':')[0]}</p>
          </div>
        )}
        
        <div className="flex justify-between items-center text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {page.createdAt ? formatDistanceToNow(new Date(page.createdAt), { addSuffix: true }) : 'Just now'}
          </span>
          <span>~{page.estimatedDuration || 15}min</span>
        </div>
      </div>
      
      {activeShift && onDutyDoctors.length > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelegateClick(page);
          }}
          className="mt-3 w-full flex items-center justify-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
        >
          <ArrowRight className="w-4 h-4" />
          Delegate Task
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <img src="/REACH_LOGO.png" alt="Reach Logo" className="h-10 sm:h-12 w-auto bg-transparent" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Doctor Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-600">Welcome, {user?.name}</p>
                {activeShift && (
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                    On Duty
                  </div>
                  <span className="text-xs text-gray-500">
                    Since {new Date(activeShift.startTime).toLocaleTimeString()}
                  </span>
                  <div className="flex items-center gap-1 text-xs">
                    <UserCheck className="w-3 h-3 text-blue-600" />
                    <span className="text-gray-600">
                      {onDutyDoctors.length} other doctor{onDutyDoctors.length !== 1 ? 's' : ''} on duty
                    </span>
                  </div>
                </div>
              )}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              {activeShift ? (
                <button
                  onClick={handleEndShiftClick}
                  className="flex items-center gap-1 sm:gap-2 bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base flex-1 sm:flex-initial justify-center"
                >
                  <PowerOff className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">End Shift</span>
                  <span className="sm:hidden">End</span>
                </button>
              ) : (
                <button
                  onClick={handleStartShift}
                  className="flex items-center gap-1 sm:gap-2 bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base flex-1 sm:flex-initial justify-center"
                >
                  <Power className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Start Shift</span>
                  <span className="sm:hidden">Start</span>
                </button>
              )}
              <button
                onClick={logout}
                className="flex items-center gap-1 sm:gap-2 bg-gray-200 text-gray-700 px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm sm:text-base flex-1 sm:flex-initial justify-center"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Eisenhower Matrix */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Eisenhower Matrix - Priority Queue</h2>
              <p className="text-sm sm:text-base text-gray-600">Tasks organised by urgency and importance</p>
            </div>
            
            {/* Review Filter */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
              <span className="text-sm text-gray-600">Show:</span>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setReviewFilter('all')}
                  className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors flex-1 sm:flex-initial ${
                    reviewFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  All ({pages.length})
                </button>
                <button
                  onClick={() => setReviewFilter('unreviewed')}
                  className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors flex-1 sm:flex-initial ${
                    reviewFilter === 'unreviewed'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Unreviewed ({pages.filter(p => !p.reviewed).length})
                </button>
                <button
                  onClick={() => setReviewFilter('reviewed')}
                  className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors flex-1 sm:flex-initial ${
                    reviewFilter === 'reviewed'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Reviewed ({pages.filter(p => p.reviewed).length})
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading pages...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* DO FIRST - Urgent & Important (Priority 1) */}
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-red-900">DO FIRST</h3>
                <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {doFirst.length} pages
                </span>
              </div>
              <p className="text-sm text-red-700 mb-4">Urgent & Important</p>
              <div className="space-y-3">
                {doFirst.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No urgent pages</p>
                ) : (
                  doFirst.map(page => <PageCard key={page.id} page={page} />)
                )}
              </div>
            </div>

            {/* SCHEDULE - Important, Not Urgent (Priority 2) */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-blue-900">SCHEDULE</h3>
                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {schedule.length} pages
                </span>
              </div>
              <p className="text-sm text-blue-700 mb-4">Important, Not Urgent</p>
              <div className="space-y-3">
                {schedule.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No scheduled pages</p>
                ) : (
                  schedule.map(page => <PageCard key={page.id} page={page} />)
                )}
              </div>
            </div>

            {/* DELEGATE - Urgent, Not Important (Priority 3) */}
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-yellow-900">DELEGATE</h3>
                <span className="bg-yellow-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {delegate.length} pages
                </span>
              </div>
              <p className="text-sm text-yellow-700 mb-4">Urgent, Not Important</p>
              <div className="space-y-3">
                {delegate.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No delegatable pages</p>
                ) : (
                  delegate.map(page => <PageCard key={page.id} page={page} />)
                )}
              </div>
            </div>

            {/* DEFER - Neither Urgent Nor Important (Priority 4) */}
            <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">DEFER</h3>
                <span className="bg-gray-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {defer.length} pages
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-4">Neither Urgent Nor Important</p>
              <div className="space-y-3">
                {defer.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No deferred pages</p>
                ) : (
                  defer.map(page => <PageCard key={page.id} page={page} />)
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Delegate Modal */}
      {showDelegateModal && selectedPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Delegate Task</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Patient:</strong> {selectedPage.patient.name} (URN: {selectedPage.patient.nhsNumber})
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Reason:</strong> {selectedPage.reason}
              </p>
            </div>
            
            <h4 className="font-semibold text-gray-900 mb-3">Select Doctor:</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
              {onDutyDoctors.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No other doctors on duty</p>
              ) : (
                onDutyDoctors.map(doctor => (
                  <button
                    key={doctor.id}
                    onClick={() => handleDelegate(doctor.id)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <UserCheck className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">{doctor.name}</p>
                        <p className="text-xs text-gray-500">{doctor.title} - {doctor.department}</p>
                        {doctor.specialties && doctor.specialties.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">{doctor.specialties.join(', ')}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            
            <button
              onClick={() => {
                setShowDelegateModal(false);
                setSelectedPage(null);
              }}
              className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* End Shift Modal */}
      {showEndShiftModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">End Shift - Delegate Open Tasks</h3>
            <p className="text-gray-600 mb-4">
              You have {pages.filter(p => ['pending', 'acknowledged', 'in_progress'].includes(p.status)).length} open task(s). 
              Please delegate them to another doctor before ending your shift.
            </p>
            
            <h4 className="font-semibold text-gray-900 mb-3">Select Doctor for All Tasks:</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
              {onDutyDoctors.length === 0 ? (
                <p className="text-red-500 text-center py-4">No other doctors on duty. Cannot end shift.</p>
              ) : (
                onDutyDoctors.map(doctor => (
                  <button
                    key={doctor.id}
                    onClick={() => handleBulkDelegate(doctor.id)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <UserCheck className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">{doctor.name}</p>
                        <p className="text-xs text-gray-500">{doctor.title} - {doctor.department}</p>
                        {doctor.specialties && doctor.specialties.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">{doctor.specialties.join(', ')}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            
            <button
              onClick={() => setShowEndShiftModal(false)}
              className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorDashboard;
